/**
 * Challan-number display. The stored value is already the canonical 10-digit
 * numeric string — no formatting or grouping is applied so what you see is
 * exactly what a bank teller / reconciliation upload / copy-paste will use.
 *
 * Kept as a function (not a passthrough at every call site) so future
 * display tweaks are one edit, not fifteen.
 */
export function formatChallanNumber(raw: string | null | undefined): string {
  return raw ? String(raw) : '';
}

export const CHALLAN_NUMBER_LENGTH = 10;

/**
 * Luhn (mod-10) check — mirror of `backend/src/utils/luhn.ts`. Kept in-repo
 * so manual-entry surfaces can reject typos before hitting the wire.
 */
function computeLuhn(body: string): string {
  if (!body || !/^\d+$/.test(body)) return '';
  let sum = 0;
  for (let i = body.length - 1, doubled = true; i >= 0; i--, doubled = !doubled) {
    let d = body.charCodeAt(i) - 48;
    if (doubled) { d *= 2; if (d > 9) d -= 9; }
    sum += d;
  }
  return String((10 - (sum % 10)) % 10);
}

/**
 * True when `id` is exactly 10 digits AND its Luhn check passes. Safe for any
 * raw user input — never throws.
 */
export function isValidChallanNumber(id: string): boolean {
  if (typeof id !== 'string' || id.length !== CHALLAN_NUMBER_LENGTH) return false;
  if (!/^\d+$/.test(id)) return false;
  return computeLuhn(id.slice(0, -1)) === id.slice(-1);
}

/**
 * Strip whitespace, hyphens, and any other non-digit decoration a human might
 * paste. Does NOT validate — call `isValidChallanNumber` on the result.
 */
export function normalizeChallanNumber(input: string | null | undefined): string {
  if (!input) return '';
  return String(input).replace(/\D/g, '');
}
